import { useCallback, useMemo, useState } from "react";
import "@pulse-ui/styling/pulse-wave-lib/components/ag-theme-pulse.css";
import "./AGOrderBook.styles.scss";

// Package Imports
import { AgGridReact } from "ag-grid-react";

// Type Imports
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColDef,
  ColumnAutoSizeModule,
  GridStateModule,
  iconOverrides,
  ModuleRegistry,
  RowApiModule,
  RowStyleModule,
  ScrollApiModule,
  SizeColumnsToContentStrategy,
  StatusPanelDef,
  themeAlpine,
} from "ag-grid-community";
import { SideBarModule, StatusBarModule } from "ag-grid-enterprise";
import AGLoadingOverlay from "../AGOverlays/AGLoadingOverlay/AGLoadingOverlay";
import { IAGOrderBookDataRow } from "@pulse-ui/types/components/AGTable";

// Utils/Helper Function Imports
import { customNumericSortFunc } from "@/utils/helper_functions";

// MUI Imports
import { Switch } from "@mui/material";

ModuleRegistry.registerModules([
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  GridStateModule,
  RowApiModule,
  RowStyleModule,
  ScrollApiModule,
  SideBarModule,
  StatusBarModule,
]);

const myTheme = themeAlpine.withPart(
  iconOverrides({
    type: "image",
    mask: true,
    icons: {
      "custom-collection-icon": {
        svg: "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' class='bi bi-collection' viewBox='0 0 16 16'><path d='M2.5 3.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1zm2-2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1zM0 13a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 16 13V6a1.5 1.5 0 0 0-1.5-1.5h-13A1.5 1.5 0 0 0 0 6zm1.5.5A.5.5 0 0 1 1 13V6a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5z'/></svg>",
      },
      "custom-three-dots-icon": {
        svg: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-three-dots-vertical" viewBox="0 0 16 16"><path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/></svg>`,
      },
    },
  })
);

const BID = "bid";
const ASK = "ask";
const MILLIONS = "Millions";
const DOLLAR = "Dollar";
const ROW_HEIGHT = 2.5;

const AGOrderBook = ({
  gridRef,
  rowData,
  loading,
}: {
  gridRef: any;
  rowData: any;
  loading?: boolean;
}) => {
  // State - START -------------------------------------
  const [millionsFormatting, setMillionsFormatting] = useState<
    typeof MILLIONS | typeof DOLLAR
  >(DOLLAR);
  // State - END ---------------------------------------

  /**
   * Calculates a specific vh value in pixels
   * @param vhValue - The vh value to convert (e.g., 3 for 3vh)
   * @returns The pixel value equivalent to the specified vh
   */
  const calculateVhInPixels = (vhValue: number): number => {
    return (window.innerHeight * vhValue) / 100;
  };

  // Custom cell renderer for zero values with formatting
  const ZeroValueRenderer = (props: any) => {
    if (props.value === "0" || props.value === 0) {
      return null;
    }

    // Apply the same formatting as priceValueFormatter
    const numValue = parseFloat(props.value);
    if (isNaN(numValue)) return <span>{props.value}</span>;

    const formatted = numValue
      .toLocaleString("en-US", {
        minimumFractionDigits: 0,
        useGrouping: true,
      })
      .replace(/,/g, ",");

    // Remove leading zeros (but keep the decimal point if it's a decimal number)
    const finalValue = formatted.replace(/^0+(?=\d)/, "");

    return <span>{finalValue}</span>;
  };

  const containerStyle = useMemo(() => {
    const rowHeight = calculateVhInPixels(ROW_HEIGHT);
    const headerHeight = calculateVhInPixels(ROW_HEIGHT);
    // Calculate exact height to fit exactly 30 rows + 1 header without scrolling
    const totalHeight =
      rowHeight * 30 + headerHeight * 2 + calculateVhInPixels(ROW_HEIGHT / 2);

    return {
      width: 1000,
      height: totalHeight,
      overflow: "hidden",
      margin: "0 auto",
    };
  }, []);

  // Value formatter for price column to add apostrophe separators at thousands
  const priceValueFormatter = (params: any) => {
    if (!params.value) return params.value;

    const numValue = parseFloat(params.value);
    if (isNaN(numValue)) return params.value;

    // Format with apostrophe separators at thousands
    const formatted = numValue
      .toLocaleString("en-US", {
        minimumFractionDigits: 0,
        useGrouping: true,
      })
      .replace(/,/g, "'");

    // Remove leading zeros (but keep the decimal point if it's a decimal number)
    return formatted.replace(/^0+(?=\d)/, "");
  };

  // Note: We're using row positions for borders instead of calculating best bid/ask

  // Row style function to highlight the spread between bids and asks
  const getRowStyle = useCallback((params: any) => {
    if (!params.data) return undefined;

    const style: any = {};

    // Add border to 15th row (last bid) - bottom border to separate from spread
    if (params.rowIndex === 14) {
      // 0-indexed, so 14 = 15th row
      style.borderBottom = "2px solid #000000";
    }

    // Add border to 16th row (first ask) - top border to mark spread
    if (params.rowIndex === 15) {
      // 0-indexed, so 15 = 16th row
      style.borderTop = "2px solid #000000";
    }

    // Return style only if we have borders to apply
    return Object.keys(style).length > 0 ? style : undefined;
  }, []);

  const initialColDefs: ColDef<IAGOrderBookDataRow>[] = [
    {
      headerName: `${
        millionsFormatting === MILLIONS ? "$Value (mm)" : "$Value"
      }`,
      field: "bids_dollar_value",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-center-aligned-header-cell",
      width: 150,
      cellStyle: (params: any) => ({
        backgroundColor: params.data.side === BID ? "#3e66a7" : "",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        borderRight: params.data.side === BID ? "1px solid #555" : "",
      }),
      cellRenderer: (params: any) =>
        params.value === "0.00"
          ? null
          : millionsFormatting === MILLIONS
          ? parseFloat(params.value) / 1000
          : params.value,
    },
    {
      headerName: "",
      field: "bid_dropcopy_qtys",
      cellClass: "ag-left-aligned-cell",
      headerClass: "ag-center-aligned-header-cell",
      width: 125,
      cellStyle: (params: any) => {
        return {
          backgroundColor: params.data.side === BID ? "#3e66a7" : "",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        };
      },
      cellRenderer: ZeroValueRenderer,
    },
    {
      headerName: "Bids",
      field: "bid_mktdata_qty",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-center-aligned-header-cell",
      width: 150,
      cellRenderer: ZeroValueRenderer,
      cellStyle: (params: any) => ({
        borderLeft: params.data.side === BID ? "1px solid #222" : "",
        fontWeight: "bold",
      }),
    },
    {
      headerName: "Price",
      field: "price",
      cellClass: "ag-center-aligned-cell",
      headerClass: "ag-center-aligned-header-cell",
      width: 150,
      valueFormatter: priceValueFormatter,
      comparator: customNumericSortFunc,
      sort: "desc",
    },
    {
      headerName: "Asks",
      field: "ask_mktdata_qty",
      cellClass: "ag-left-aligned-cell",
      headerClass: "ag-center-aligned-header-cell",
      cellRenderer: ZeroValueRenderer,
      width: 150,
      cellStyle: (params: any) => ({
        borderRight: params.data.side === ASK ? "1px solid #0c1835" : "",
        fontWeight: "bold",
      }),
    },
    {
      headerName: "",
      field: "ask_dropcopy_qtys",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-center-aligned-header-cell",
      width: 125,
      cellStyle: (params: any) => ({
        backgroundColor: params.data.side === ASK ? "#e70a16" : "",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }),
      cellRenderer: ZeroValueRenderer,
    },
    {
      headerName: `${
        millionsFormatting === MILLIONS ? "$Value (mm)" : "$Value"
      }`,
      field: "asks_dollar_value",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-center-aligned-header-cell",
      width: 150,
      cellStyle: (params: any) => ({
        backgroundColor: params.data.side === ASK ? "#e70a16" : "",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        borderLeft: params.data.side === ASK ? "1px solid #0c1835" : "",
      }),
      cellRenderer: (params: any) =>
        params.value === "0.00"
          ? null
          : millionsFormatting === MILLIONS
          ? parseFloat(params.value) / 1000
          : params.value,
    },
  ];

  const colDefs = useMemo<ColDef<IAGOrderBookDataRow>[]>(
    () => initialColDefs,
    [millionsFormatting]
  );

  const defaultColDef: ColDef = {
    filter: false,
    sortable: false,
    resizable: true,
  };

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(() => {
    return {
      statusPanels: [
        {
          statusPanel: () => {
            return (
              <div className="status-bar-switch-container pulse-row pulse-align-center">
                <p className="pulse-typography-subtitle-1 pulse-color--background-100">
                  Dollars
                </p>
                <Switch
                  checked={millionsFormatting === MILLIONS}
                  onChange={() =>
                    setMillionsFormatting(
                      millionsFormatting === MILLIONS ? DOLLAR : MILLIONS
                    )
                  }
                />
                <p className="pulse-typography-subtitle-1 pulse-color--background-100">
                  Millions
                </p>
              </div>
            );
          },
          align: "left",
          height: calculateVhInPixels(ROW_HEIGHT),
        },
      ],
    };
  }, [millionsFormatting]);

  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  const getRowId = useCallback((params: any) => params.data.price, []);

  return (
    <div className="ag-theme-alpine ag-theme-pulse" style={containerStyle}>
      <AgGridReact
        ref={gridRef}
        rowData={rowData}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        autoSizeStrategy={autoSizeStrategy}
        sideBar={false}
        animateRows={false}
        getRowId={getRowId}
        getRowStyle={getRowStyle}
        loadingOverlayComponent={AGLoadingOverlay}
        loading={loading || rowData.length === 0 || false}
        theme={myTheme}
        rowHeight={calculateVhInPixels(ROW_HEIGHT)}
        headerHeight={calculateVhInPixels(ROW_HEIGHT)}
        suppressScrollOnNewData={true}
        suppressHorizontalScroll={false}
        statusBar={statusBar}
      />
    </div>
  );
};

export default AGOrderBook;
